import { useEffect, type ReactNode } from "react";
import {
  WbButton,
  WbHeading,
  WbIcon,
  WbSpinner,
  WbText,
} from "@workbench/react";
import { useGetCurrentUserQuery } from "../../store/authApi";
import "../../styles/AuthGate.css";

interface AuthGateProps {
  children: ReactNode;
}

function getStatus(error: unknown): number | undefined {
  if (typeof error === "object" && error !== null && "status" in error) {
    const status = (error as { status?: unknown }).status;
    return typeof status === "number" ? status : undefined;
  }
  return undefined;
}

export default function AuthGate({ children }: AuthGateProps) {
  const authError = new URLSearchParams(window.location.search).get(
    "authError",
  );
  const { isLoading, isFetching, error, refetch } = useGetCurrentUserQuery();

  useEffect(() => {
    if (authError !== "access_denied") return;

    const url = new URL(window.location.href);
    url.searchParams.delete("authError");
    window.history.replaceState(
      null,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, [authError]);

  if (isLoading || isFetching) {
    return (
      <AuthStateCard
        tone="neutral"
        title="Preparing sign-in"
        description="We are verifying your session before continuing."
        showSpinner
      />
    );
  }

  const status = getStatus(error);

  if (status === 401) {
    return (
      <AuthStateCard
        tone="neutral"
        title="Redirecting to sign-in"
        description="You will be forwarded to the Mercedes-Benz authentication service."
        showSpinner
      />
    );
  }

  if (status === 403) {
    return <AccessUnavailableCard />;
  }

  if (error) {
    return (
      <AuthStateCard
        tone="error"
        title="Sign-in incomplete"
        description="We could not verify your session. Please try again."
      >
        <div className="wb-margin-top-xs">
          <WbButton variant="secondary" size="s" onClick={() => refetch()}>
            <span className="auth-state__button-content">
              <span>Sign in again</span>
              <WbIcon name="bds/arrow-right/24" />
            </span>
          </WbButton>
        </div>
      </AuthStateCard>
    );
  }

  return <>{children}</>;
}

function AccessUnavailableCard() {
  return (
    <AuthStateCard
      tone="denied"
      title="You don't have access"
      description="Your account doesn't have permission to access this application."
    >
      <div className="wb-margin-top-xs">
        <WbButton
          variant="secondary"
          size="s"
          onClick={() => window.location.assign("/api/auth/login")}
        >
          <span className="auth-state__button-content">
            <WbIcon name="bds/refresh/24" />
            <span>Try again</span>
          </span>
        </WbButton>
      </div>
    </AuthStateCard>
  );
}

export function AuthStateCard({
  children,
  description,
  detail,
  loadingLabel,
  showSpinner = false,
  title,
  tone,
}: {
  children?: ReactNode;
  description?: string;
  detail?: string;
  loadingLabel?: string;
  showSpinner?: boolean;
  title: string;
  tone: "neutral" | "denied" | "error";
}) {
  return (
    <main className="auth-page wb-padding-horizontal-m">
      <section className={`auth-state auth-state--${tone}`}>
        <header className="auth-state__header wb-padding-horizontal-s wb-padding-top-xxs wb-padding-bottom-xxs">
          <BrandMark />
        </header>
        <div className="auth-state__body wb-padding-horizontal-s wb-padding-bottom-s">
          {showSpinner && <WbSpinner size="l" role="status" aria-label={title} />}
          <WbHeading tag="h1" size={tone === "neutral" ? "m" : "l"}>
            {title}
          </WbHeading>
          {description && <WbText size="m">{description}</WbText>}
          {detail && <WbText size="s">{detail}</WbText>}
          {children}
        </div>
      </section>
    </main>
  );
}

function BrandMark() {
  return (
    <div className="auth-state__brand" aria-label="Mercedes-Benz">
      <WbIcon name="bds/mb-star/24" />
      <span className="auth-state__brand-divider" aria-hidden="true" />
      <span className="auth-state__brand-name">eCON Pulse</span>
    </div>
  );
}
